import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Button, InputNumber, Space, theme } from 'antd';
import type { EventBus, PDFViewer } from 'pdfjs-dist/web/pdf_viewer.mjs';
import type React from 'react';
import { useEffect, useState } from 'react';

interface Props {
  viewer: PDFViewer | null;
  eventBus: EventBus | null;
}

// Fixed so the group keeps its width across documents; sized for three digits.
const pageInputStyle = css`
  width: 52px;
`;

// Right-aligned so the number sits against the "/ N" that follows it, as the old viewer
// did. Applied as an inline style because antd's own `.ant-input-number
// .ant-input-number-input { text-align: start }` is a descendant selector and outranks a
// single class of ours.
const pageInputFieldStyle = { textAlign: 'right' } as const;

export const PageControls: React.FC<Props> = ({ viewer, eventBus }) => {
  const { token } = theme.useToken();
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  // Non-null only while the field is being edited, so typing "12" does not jump to
  // page 1 on the first keystroke. Committed on Enter or blur.
  const [draft, setDraft] = useState<string | null>(null);

  useEffect(() => {
    if (!eventBus || !viewer) {
      return;
    }
    const onPagesInit = () => {
      setTotal(viewer.pagesCount);
      setPage(viewer.currentPageNumber);
    };
    const onPageChanging = ({ pageNumber }: { pageNumber: number }) => setPage(pageNumber);

    eventBus.on('pagesinit', onPagesInit);
    eventBus.on('pagechanging', onPageChanging);
    // The document may already be set by the time this effect runs.
    if (viewer.pagesCount > 0) {
      onPagesInit();
    }
    return () => {
      eventBus.off('pagesinit', onPagesInit);
      eventBus.off('pagechanging', onPageChanging);
    };
  }, [eventBus, viewer]);

  const goTo = (target: number) => {
    if (viewer && target >= 1 && target <= total) {
      viewer.currentPageNumber = target;
    }
  };

  const commitDraft = () => {
    if (draft === null) {
      return;
    }
    const parsed = Number.parseInt(draft, 10);
    if (Number.isFinite(parsed)) {
      goTo(Math.min(Math.max(parsed, 1), total));
    }
    // Clearing the draft falls back to the viewer's page, so an empty or out-of-range
    // entry simply snaps back instead of leaving the field in a bogus state.
    setDraft(null);
  };

  const labelStyle = css`
    color: ${token.colorText};
    white-space: nowrap;
  `;

  return (
    <Space size={token.paddingXS}>
      <Button icon={<LeftOutlined />} aria-label="Vorherige Seite" disabled={!viewer || page <= 1} onClick={() => goTo(page - 1)} />
      <Button icon={<RightOutlined />} aria-label="Nächste Seite" disabled={!viewer || page >= total} onClick={() => goTo(page + 1)} />
      <span className={labelStyle}>Seite</span>
      <InputNumber
        className={pageInputStyle}
        styles={{ input: pageInputFieldStyle }}
        aria-label="Seite"
        controls={false}
        min={1}
        max={total || 1}
        value={draft ?? page}
        disabled={!viewer || total === 0}
        onChange={value => setDraft(value === null ? '' : String(value))}
        onPressEnter={commitDraft}
        onBlur={commitDraft}
      />
      <span className={labelStyle}>/ {total || '–'}</span>
    </Space>
  );
};
